/**
 * PartnerNextBestAction — dynamic guidance card on the Partner Dashboard.
 *
 * Picks ONE highest-priority action based on the partner's current state
 * and shows it as a CTA. As the partner progresses, the card automatically
 * promotes the next action — guiding them step by step toward more
 * recurring revenue.
 *
 * Priority (highest → lowest):
 *   1. Stripe not connected           → "Connect your Stripe account"
 *   2. No referrals yet               → "Invite your first restaurant"
 *   3. Has restaurants in trial       → "Follow up with restaurants in trial"
 *   4. Few referrals (<5)             → "Share your referral link"
 *   5. Has active restaurants         → "Request a marketing campaign"
 *   6. Fallback                       → "Share your referral link"
 */
import React from 'react';
import { Link } from 'react-router-dom';
import {
  CreditCard,
  UserPlus,
  Share2,
  Bell,
  Megaphone,
  ArrowRight,
} from 'lucide-react';

const STEPS = [
  {
    id: 'connect-stripe',
    title: 'Connect your Stripe account',
    desc: 'NoFee uses Stripe Connect to send your recurring revenue directly to your organization. This takes about 3 minutes.',
    cta: 'Connect Stripe',
    to: '/partners/account/stripe',
    Icon: CreditCard,
    accent: 'from-[#635BFF]/30 to-[#635BFF]/5 border-[#635BFF]/40 text-[#A89EFF]',
  },
  {
    id: 'invite-first',
    title: 'Invite your first restaurant',
    desc: 'Send your referral link to one restaurant in your network. The moment they activate, you start earning 10% recurring revenue every month.',
    cta: 'Get my referral link',
    to: '/partners/account/referral',
    Icon: UserPlus,
    accent: 'from-emerald-500/25 to-emerald-500/5 border-emerald-500/40 text-emerald-300',
  },
  {
    id: 'follow-up-trial',
    title: 'Follow up with restaurants in trial',
    desc: 'You have member restaurants still in their 45-day trial. A quick check-in dramatically increases the chance they convert into recurring revenue.',
    cta: 'View member restaurants',
    to: '/partners/account/restaurants',
    Icon: Bell,
    accent: 'from-amber-500/25 to-amber-500/5 border-amber-500/40 text-amber-300',
  },
  {
    id: 'share-link',
    title: 'Share your referral link',
    desc: 'You\'re off to a strong start. Sharing your link with more restaurants in your network compounds your monthly recurring revenue.',
    cta: 'Open referral kit',
    to: '/partners/account/referral',
    Icon: Share2,
    accent: 'from-gold-500/25 to-gold-500/5 border-gold-500/40 text-gold-300',
  },
  {
    id: 'request-campaign',
    title: 'Request a marketing campaign',
    desc: 'Your active member restaurants are earning you recurring revenue every month. Ask NoFee to launch a co-branded campaign to grow that base even faster.',
    cta: 'Open Marketing Support',
    to: '/partners/account/marketing',
    Icon: Megaphone,
    accent: 'from-fuchsia-500/25 to-fuchsia-500/5 border-fuchsia-500/40 text-fuchsia-300',
  },
];

function pickAction({ stripeConnected, totalReferred, trialRestaurants, activeRestaurants }) {
  if (!stripeConnected) return STEPS[0];
  if ((totalReferred || 0) === 0) return STEPS[1];
  if ((trialRestaurants || 0) > 0) return STEPS[2];
  if ((totalReferred || 0) < 5) return STEPS[3];
  if ((activeRestaurants || 0) > 0) return STEPS[4];
  return STEPS[3];
}

const PartnerNextBestAction = ({ partner, overview }) => {
  const action = pickAction({
    stripeConnected: !!partner?.stripe_details_submitted,
    totalReferred: overview?.total_referred,
    trialRestaurants: overview?.trial_restaurants,
    activeRestaurants: overview?.active_restaurants,
  });

  return (
    <section
      data-testid="partner-next-best-action"
      data-action-id={action.id}
      className="mb-8 rounded-2xl border border-white/[0.08] bg-gradient-to-br from-zinc-950 via-black to-zinc-950 p-5 sm:p-6"
    >
      <div className="flex items-center gap-2 mb-4">
        <span className="text-lg" role="img" aria-label="star">⭐</span>
        <h2 className="text-[15px] sm:text-[16px] font-extrabold text-white tracking-tight">
          Next Best Action
        </h2>
      </div>

      <div
        className={`rounded-2xl border bg-gradient-to-br ${action.accent} p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-5`}
      >
        <div className="flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-black/40 border border-white/15 flex items-center justify-center">
          <action.Icon className="w-6 h-6 sm:w-7 sm:h-7" />
        </div>
        <div className="flex-1 min-w-0">
          <h3
            data-testid="partner-nba-title"
            className="text-[16px] sm:text-[17.5px] font-extrabold text-white tracking-tight leading-snug"
          >
            {action.title}
          </h3>
          <p
            data-testid="partner-nba-desc"
            className="mt-1.5 text-[13px] text-white/70 leading-relaxed"
          >
            {action.desc}
          </p>
        </div>
        <Link
          to={action.to}
          data-testid="partner-nba-cta"
          className="flex-shrink-0 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-white text-black hover:bg-white/90 text-[13px] font-extrabold transition-all whitespace-nowrap"
        >
          {action.cta}
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </section>
  );
};

export default PartnerNextBestAction;
